"use client";

import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCameraContext } from "@/context/camera-context";
import { useSelectedCamera } from "@/context/selected-camera-context";
import { columns } from "./columns";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

/** Names shown in the column picker for the columns a user can hide. */
const COLUMN_LABEL_KEYS: Record<string, TranslationKey> = {
  id: "cameras.columns.deviceId",
  name: "common.name",
  status: "common.status",
  branch_uuid: "cameras.columns.branchUuid",
  branch_name: "common.branch",
  assigned: "cameras.assignment.assigned",
  token: "cameras.columns.token",
};

type CamerasProps = {
  getLink: (deviceId: string) => string;
};

export default function Cameras({ getLink }: CamerasProps) {
  const { t } = useLocalization();
  const { setSelectedDeviceId } = useSelectedCamera();
  const {
    cameras,
    page,
    setSortBy,
    setSortOrder,
    setPage,
    perPage,
    setPerPage,
    totalPages,
    deviceIdFilter,
    setDeviceIdFilter,
    nameFilter,
    setNameFilter,
    assignedFilter,
    setAssignedFilter,
  } = useCameraContext();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      deviceId: true,
      name: true,
      assigned: true,
      branch_uuid: true,
      token: true,
      detail: true,
    });
  const [rowSelection, setRowSelection] = React.useState({});

  useEffect(() => {
    if (sorting.length === 0) return;

    setSortBy(sorting.length ? sorting[0].id : "name");
    setSortOrder(sorting.length && sorting[0].desc ? "desc" : "asc");
  }, [sorting, setSortBy, setSortOrder]);

  const table = useReactTable({
    data: cameras ?? [],
    columns: columns(getLink),
    pageCount: totalPages,
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newPage =
        typeof updater === "function"
          ? updater({ pageIndex: page, pageSize: perPage }).pageIndex
          : updater;
      if (typeof newPage === "number") {
        setPage(newPage);
      }
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: page,
        pageSize: perPage,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (rowSelection) => {
      setRowSelection(rowSelection);
      const selectedRow =
        Object.keys(rowSelection).length && cameras
          ? cameras[Number(Object.keys(rowSelection)[0])]
          : null;

      if (selectedRow) {
        setSelectedDeviceId(selectedRow.id);
      }
    },
  });

  return (
    <div className="w-full gap-4 flex flex-col">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("cameras.list.deviceIdFilter")}
          value={deviceIdFilter}
          onChange={(event) => {
            const value = event.target.value;
            setDeviceIdFilter(value.trim());
          }}
          className="max-w-sm"
        />
        <Input
          placeholder={t("cameras.list.nameFilter")}
          value={nameFilter}
          onChange={(event) => {
            const value = event.target.value;
            setNameFilter(value.trim());
          }}
          className="max-w-sm"
        />
        <Select
          value={assignedFilter === null ? "all" : assignedFilter.toString()}
          onValueChange={(value) => {
            if (value === "all") {
              setAssignedFilter(null);
            } else {
              setAssignedFilter(value === "true");
            }
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t("cameras.columns.assignment")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="true">{t("cameras.assignment.assigned")}</SelectItem>
            <SelectItem value="false">{t("cameras.assignment.unassigned")}</SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              {t("common.columns")} <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {COLUMN_LABEL_KEYS[column.id]
                      ? t(COLUMN_LABEL_KEYS[column.id])
                      : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("common.noResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between space-x-2">
        <div className="flex w-32 items-center justify-center text-sm font-medium">
          {t("common.page", {
            page: table.getState().pagination.pageIndex + 1,
            pages: table.getPageCount(),
          })}
        </div>
        <div className="ml-auto flex flex-grow justify-center items-center gap-2 lg:ml-0">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              table.setPageIndex(0);
            }}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t("common.firstPage")}</span>
            <ChevronsLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="size-8"
            size="icon"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t("common.previousPage")}</span>
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="size-8"
            size="icon"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t("common.nextPage")}</span>
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outline"
            className="hidden size-8 lg:flex"
            size="icon"
            onClick={() => {
              table.setPageIndex(table.getPageCount() - 1);
            }}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t("common.lastPage")}</span>
            <ChevronsRightIcon />
          </Button>
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <Label htmlFor="rows-per-page" className="text-sm font-medium">
            {t("common.perPage")}
          </Label>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              setPerPage(Number(value));
            }}
          >
            <SelectTrigger className="w-20" id="rows-per-page">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
